/**
 * Post-acceptance project ledger — financial roll-up + change
 * orders + payments + job-status controls.
 *
 * Renders on the QuoteDetailPage after the quote is accepted. The
 * pre-acceptance UI (line-item editor, send button, status journey)
 * is replaced by a project-running view: what's owed, what's been
 * received, where the project is in its lifecycle, what changes
 * have been authorized since acceptance.
 *
 * Three numbered ledgers stack vertically:
 *
 *   1. Financial KPIs — Committed (quote total + accepted COs) /
 *      Received / Balance
 *   2. Change orders — additions + credits, with per-row sign status
 *   3. Payments — receipts with running balance
 *
 * Plus a "Job lifecycle" block above the ledgers letting the
 * contractor flip the parent Job between won → in_progress →
 * complete + set a scheduled-for date.
 */
import { CheckCircle2, Eye, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useChangeOrders,
  useCreateChangeOrder,
  useCreatePayment,
  useDeleteChangeOrder,
  useDeletePayment,
  usePayments,
  useUpdateChangeOrder,
  useUpdateJob,
} from "@/api/hooks";
import type {
  ChangeOrderResponse,
  JobDetail,
  PaymentMethod,
  PaymentScheduleEntry,
  ProjectPaymentResponse,
  QuoteResponse,
} from "@/api/types";
import { formatCents, formatTime } from "@/lib/format";

// ---------------------------------------------------------------
// Top-level
// ---------------------------------------------------------------

export function ProjectLedger({
  quote,
  job,
}: {
  quote: QuoteResponse;
  job: JobDetail;
}) {
  const changeOrdersQuery = useChangeOrders(quote.id);
  const paymentsQuery = usePayments(quote.id);

  const changeOrders = changeOrdersQuery.data ?? [];
  const payments = paymentsQuery.data ?? [];

  // Committed = quote total + sum of ACCEPTED change orders. Drafts
  // and sent-but-unsigned COs don't count yet — they're proposals,
  // not authorized work.
  const acceptedCoTotal = changeOrders
    .filter((co) => co.status === "accepted")
    .reduce((sum, co) => sum + co.amount_cents, 0);
  const committed = quote.total_cents + acceptedCoTotal;

  const received = payments.reduce((sum, p) => sum + p.amount_cents, 0);
  const balance = committed - received;

  return (
    <div className="space-y-6">
      <FinancialSummary
        committed={committed}
        received={received}
        balance={balance}
        quoteTotal={quote.total_cents}
        coTotal={acceptedCoTotal}
        coCount={
          changeOrders.filter((co) => co.status === "accepted").length
        }
      />

      {quote.payment_schedule_snapshot && quote.payment_schedule_snapshot.length > 0 && (
        <PaymentScheduleBlock schedule={quote.payment_schedule_snapshot} />
      )}

      <JobLifecycleControls job={job} />

      <ChangeOrdersBlock
        quoteId={quote.id}
        changeOrders={changeOrders}
        loading={changeOrdersQuery.isLoading}
      />

      <PaymentsBlock
        quoteId={quote.id}
        payments={payments}
        committed={committed}
        loading={paymentsQuery.isLoading}
      />
    </div>
  );
}

// ---------------------------------------------------------------
// 1. Financial summary
// ---------------------------------------------------------------

function FinancialSummary({
  committed,
  received,
  balance,
  quoteTotal,
  coTotal,
  coCount,
}: {
  committed: number;
  received: number;
  balance: number;
  quoteTotal: number;
  coTotal: number;
  coCount: number;
}) {
  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <KpiCard
        label="Committed"
        value={formatCents(committed)}
        sub={
          coCount > 0
            ? `${formatCents(quoteTotal)} quote · ${formatCents(coTotal)} change orders`
            : `${formatCents(quoteTotal)} quote · no change orders`
        }
        emphasis="primary"
      />
      <KpiCard
        label="Received"
        value={formatCents(received)}
        sub={
          received === 0
            ? "No payments recorded yet"
            : received >= committed
              ? "Paid in full"
              : `${Math.round((received / Math.max(committed, 1)) * 100)}% of committed`
        }
        emphasis={received >= committed ? "success" : "neutral"}
      />
      <KpiCard
        label="Balance"
        value={formatCents(balance)}
        sub={
          balance === 0
            ? "Project is fully paid."
            : balance < 0
              ? "Overpaid (refund / credit owed)"
              : "Outstanding to collect"
        }
        emphasis={balance === 0 ? "success" : balance < 0 ? "warning" : "primary"}
      />
    </section>
  );
}

function KpiCard({
  label,
  value,
  sub,
  emphasis,
}: {
  label: string;
  value: string;
  sub: string;
  emphasis: "primary" | "success" | "warning" | "neutral";
}) {
  const tone =
    emphasis === "success"
      ? "border-pencil-yellow/40 bg-pencil-yellow-soft"
      : emphasis === "warning"
        ? "border-burnt/30 bg-burnt/10"
        : "border-rule bg-paper-warm";
  return (
    <div className={`rounded-lg border p-4 ${tone}`}>
      <span className="label-mono">{label}</span>
      <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-graphite">
        {value}
      </p>
      <p className="mt-1 font-mono text-[11px] text-graphite-soft">{sub}</p>
    </div>
  );
}

// ---------------------------------------------------------------
// 1.5. Payment schedule (snapshot)
// ---------------------------------------------------------------

function PaymentScheduleBlock({ schedule }: { schedule: PaymentScheduleEntry[] }) {
  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-baseline justify-between">
        <span className="label-mono">Payment schedule</span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-faint">
          Frozen on accept
        </span>
      </div>
      <ul className="mt-3 divide-y divide-rule">
        {schedule.map((entry, i) => (
          <li
            key={`${entry.label}-${i}`}
            className="flex items-center justify-between gap-4 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm font-semibold text-graphite">
                {entry.label}
                <span className="ml-2 font-mono text-xs text-graphite-soft">
                  ({Math.round(entry.percent * 100)}%)
                </span>
              </p>
              {entry.trigger && (
                <p className="font-mono text-xs text-graphite-soft">
                  {entry.trigger}
                </p>
              )}
            </div>
            <span className="font-mono text-sm tabular-nums text-graphite">
              {formatCents(entry.amount_cents)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------
// 2. Job lifecycle controls
// ---------------------------------------------------------------

function JobLifecycleControls({ job }: { job: JobDetail }) {
  const update = useUpdateJob(job.id);
  const [scheduledFor, setScheduledFor] = useState<string>(
    job.scheduled_for ? job.scheduled_for.slice(0, 10) : "",
  );

  const isInProgress = job.status === "in_progress";
  const isComplete = job.status === "complete";

  function setStatus(next: "won" | "in_progress" | "complete") {
    update.mutate({ status: next });
  }

  function commitScheduledFor() {
    const trimmed = scheduledFor.trim();
    update.mutate({
      scheduled_for: trimmed
        ? new Date(trimmed + "T09:00:00").toISOString()
        : null,
    });
  }

  return (
    <section className="rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="label-mono">Project status</span>
          <p className="mt-1 font-mono text-sm font-semibold text-graphite">
            {labelForJobStatus(job.status)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isInProgress && !isComplete && (
            <button
              type="button"
              onClick={() => setStatus("in_progress")}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft"
            >
              Mark in progress
            </button>
          )}
          {isInProgress && (
            <button
              type="button"
              onClick={() => setStatus("complete")}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft"
            >
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Mark complete
            </button>
          )}
          {isComplete && (
            <span className="inline-flex items-center gap-1 rounded border border-pencil-yellow/40 bg-pencil-yellow-soft px-3 py-1.5 font-mono text-xs font-semibold text-graphite">
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Project complete
            </span>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="font-mono text-xs uppercase tracking-wider text-graphite-soft">
          Scheduled for
          <input
            type="date"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            onBlur={commitScheduledFor}
            className="ml-2 rounded border border-rule bg-paper px-2 py-1 font-mono text-sm normal-case text-graphite focus:border-graphite focus:outline-none"
          />
        </label>
        {job.scheduled_for && (
          <span className="font-mono text-xs text-graphite-soft">
            (Currently {formatTime(job.scheduled_for)})
          </span>
        )}
      </div>
    </section>
  );
}

function labelForJobStatus(s: JobDetail["status"]): string {
  switch (s) {
    case "won":
      return "Won — awaiting kickoff";
    case "in_progress":
      return "In progress";
    case "complete":
      return "Complete";
    case "lost":
      return "Lost";
    case "archived":
      return "Archived";
    case "open":
    case "quoted":
    default:
      return s;
  }
}

// ---------------------------------------------------------------
// 3. Change orders
// ---------------------------------------------------------------

function ChangeOrdersBlock({
  quoteId,
  changeOrders,
  loading,
}: {
  quoteId: string;
  changeOrders: ChangeOrderResponse[];
  loading: boolean;
}) {
  const create = useCreateChangeOrder(quoteId);
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftAmount, setDraftAmount] = useState("");
  const [draftDescription, setDraftDescription] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function reset() {
    setDraftTitle("");
    setDraftAmount("");
    setDraftDescription("");
    setErrorMsg(null);
    setAdding(false);
  }

  async function submit() {
    setErrorMsg(null);
    if (!draftTitle.trim()) {
      setErrorMsg("Title is required.");
      return;
    }
    const numeric = Number(draftAmount);
    if (!Number.isFinite(numeric)) {
      setErrorMsg("Amount must be a number.");
      return;
    }
    try {
      await create.mutateAsync({
        title: draftTitle.trim(),
        amount_cents: Math.round(numeric * 100),
        description: draftDescription.trim() || null,
      });
      reset();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not create.");
    }
  }

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Change orders
          {changeOrders.length > 0 && (
            <span className="ml-2 normal-case text-graphite-faint">
              ({changeOrders.length})
            </span>
          )}
        </h3>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Add change order
          </button>
        )}
      </div>

      {loading ? (
        <p className="font-mono text-xs text-graphite-soft">Loading…</p>
      ) : changeOrders.length === 0 && !adding ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No change orders. Add one when the homeowner authorizes
          additional work or a credit.
        </p>
      ) : (
        <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-warm">
          {changeOrders.map((co) => (
            <ChangeOrderRow key={co.id} co={co} quoteId={quoteId} />
          ))}
        </ul>
      )}

      {adding && (
        <div className="rounded-lg border-2 border-graphite bg-paper p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px]">
            <label className="block">
              <span className="label-mono">Title</span>
              <input
                type="text"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Add walk-in shower glass enclosure"
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="label-mono">Amount ($)</span>
              <input
                type="number"
                step="0.01"
                value={draftAmount}
                onChange={(e) => setDraftAmount(e.target.value)}
                placeholder="-250.00 = credit"
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
              />
            </label>
          </div>
          <label className="block">
            <span className="label-mono">Description (optional)</span>
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
            />
          </label>
          {errorMsg && (
            <p className="font-mono text-xs text-red-pencil">{errorMsg}</p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={create.isPending}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {create.isPending ? "Saving…" : "Add"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Per-CO preview button: links to the parent quote's
 *  customer-preview view, where the contractor can see exactly
 *  what the homeowner will see (CO list + Approve/Decline UI,
 *  rendered read-only on the preview surface). Hash anchors the
 *  page so the right CO row scrolls into focus. */
function ChangeOrderPreviewLink({
  quoteId,
  coId,
}: {
  quoteId: string;
  coId: string;
}) {
  return (
    <Link
      to={`/quotes/${quoteId}/preview#co-${coId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft hover:text-graphite"
      title="See what the homeowner sees — opens in a new tab"
    >
      <Eye className="mr-0.5 inline h-3 w-3" aria-hidden /> Preview
    </Link>
  );
}

function ChangeOrderRow({
  co,
  quoteId,
}: {
  co: ChangeOrderResponse;
  quoteId: string;
}) {
  const update = useUpdateChangeOrder(quoteId, co.id);
  const remove = useDeleteChangeOrder(quoteId);
  const isCredit = co.amount_cents < 0;
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(co.title);
  const [editAmount, setEditAmount] = useState(
    (co.amount_cents / 100).toFixed(2),
  );
  const [editDescription, setEditDescription] = useState(co.description ?? "");
  const [editError, setEditError] = useState<string | null>(null);

  const statusTone = {
    draft: "bg-paper text-graphite-soft border-rule",
    sent: "bg-pencil-yellow-soft text-graphite border-pencil-yellow/40",
    accepted: "bg-pencil-yellow-soft text-graphite border-pencil-yellow/40",
    declined: "bg-burnt/10 text-burnt border-burnt/30",
  }[co.status];

  function flipToSent() {
    update.mutate({ status: "sent" });
  }
  function unsend() {
    if (
      !window.confirm(
        `Unsend CO #${co.number}? The homeowner's email link will stop working and the CO will move back to draft. You can edit and re-send.`,
      )
    )
      return;
    update.mutate({ status: "draft" });
  }
  function deleteCo() {
    if (
      !window.confirm(
        `Delete CO #${co.number} permanently? This removes it from the project ledger entirely.`,
      )
    )
      return;
    remove.mutate(co.id);
  }
  function beginEdit() {
    setEditTitle(co.title);
    setEditAmount((co.amount_cents / 100).toFixed(2));
    setEditDescription(co.description ?? "");
    setEditError(null);
    setEditing(true);
  }
  async function saveEdit() {
    setEditError(null);
    if (!editTitle.trim()) {
      setEditError("Title is required.");
      return;
    }
    const numeric = Number(editAmount);
    if (!Number.isFinite(numeric)) {
      setEditError("Amount must be a number.");
      return;
    }
    try {
      await update.mutateAsync({
        title: editTitle.trim(),
        amount_cents: Math.round(numeric * 100),
        description: editDescription.trim() || null,
      });
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.detail : "Could not save.");
    }
  }

  // Edit view — only reachable from a draft CO (button hidden in
  // sent/accepted/declined states). Same field shape as the "Add"
  // form above so the contractor's mental model is consistent.
  if (editing) {
    return (
      <li className="border-2 border-graphite bg-paper px-4 py-3 space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px]">
          <label className="block">
            <span className="label-mono">Title</span>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="label-mono">Amount ($)</span>
            <input
              type="number"
              step="0.01"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
            />
          </label>
        </div>
        <label className="block">
          <span className="label-mono">Description</span>
          <textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
          />
        </label>
        {editError && <p className="font-mono text-xs text-red-pencil">{editError}</p>}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="rounded border border-rule px-3 py-1.5 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveEdit}
            disabled={update.isPending}
            className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper hover:bg-graphite-soft disabled:opacity-50"
          >
            {update.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-mono text-xs font-semibold text-graphite-soft">
            CO #{co.number}
          </span>
          <span className="text-sm font-medium text-graphite">{co.title}</span>
          <span
            className={`inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider ${statusTone}`}
          >
            {co.status}
          </span>
        </div>
        {co.description && (
          <p className="mt-1 text-xs text-graphite-soft">{co.description}</p>
        )}
        <p className="mt-1 font-mono text-[11px] text-graphite-faint">
          {co.status === "accepted" && co.signed_name
            ? `Signed by ${co.signed_name} ${formatTime(co.accepted_at)}`
            : co.status === "sent"
              ? `Sent ${formatTime(co.sent_at)} — awaiting homeowner`
              : co.status === "declined"
                ? `Declined ${formatTime(co.declined_at)}`
                : `Draft — created ${formatTime(co.created_at)}`}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span
          className={`font-mono text-sm font-semibold tabular-nums ${
            isCredit ? "text-burnt" : "text-graphite"
          }`}
        >
          {isCredit ? "−" : "+"}
          {formatCents(Math.abs(co.amount_cents))}
        </span>

        {/* Action affordances by lifecycle:
              draft     → Edit · Send · Delete (permanent)
              sent      → Unsend (back to draft) — homeowner's email
                          link will stop working; CO no longer renders
                          on the public view
              accepted  → no actions (terminal record; signature stands)
              declined  → no actions (terminal; create a new CO if needed)
        */}
        {co.status === "draft" && (
          <div className="flex items-center gap-2">
            <ChangeOrderPreviewLink quoteId={quoteId} coId={co.id} />
            <button
              type="button"
              onClick={beginEdit}
              className="font-mono text-[10px] uppercase tracking-wider text-graphite-soft hover:text-graphite"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={flipToSent}
              disabled={update.isPending}
              className="rounded bg-graphite px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-paper hover:bg-graphite-soft disabled:opacity-50"
            >
              Send
            </button>
            <button
              type="button"
              onClick={deleteCo}
              className="text-graphite-soft hover:text-red-pencil"
              title={`Delete CO #${co.number}`}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        )}
        {co.status === "sent" && (
          <div className="flex items-center gap-2">
            <ChangeOrderPreviewLink quoteId={quoteId} coId={co.id} />
            <button
              type="button"
              onClick={unsend}
              disabled={update.isPending}
              className="font-mono text-[10px] uppercase tracking-wider text-burnt hover:text-graphite disabled:opacity-50"
              title="Move back to draft. The homeowner's email link will stop working."
            >
              Unsend / void
            </button>
          </div>
        )}
        {(co.status === "accepted" || co.status === "declined") && (
          <ChangeOrderPreviewLink quoteId={quoteId} coId={co.id} />
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------
// 4. Payments
// ---------------------------------------------------------------

function PaymentsBlock({
  quoteId,
  payments,
  committed,
  loading,
}: {
  quoteId: string;
  payments: ProjectPaymentResponse[];
  committed: number;
  loading: boolean;
}) {
  const create = useCreatePayment(quoteId);
  const remove = useDeletePayment(quoteId);
  const [adding, setAdding] = useState(false);
  const [draftAmount, setDraftAmount] = useState("");
  const [draftMethod, setDraftMethod] = useState<PaymentMethod>("check");
  const [draftDate, setDraftDate] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function reset() {
    setDraftAmount("");
    setDraftMethod("check");
    setDraftDate("");
    setDraftNotes("");
    setErrorMsg(null);
    setAdding(false);
  }

  async function submit() {
    setErrorMsg(null);
    const numeric = Number(draftAmount);
    if (!Number.isFinite(numeric) || numeric === 0) {
      setErrorMsg("Amount must be a non-zero number.");
      return;
    }
    try {
      await create.mutateAsync({
        amount_cents: Math.round(numeric * 100),
        method: draftMethod,
        received_at: draftDate
          ? new Date(draftDate + "T12:00:00").toISOString()
          : null,
        notes: draftNotes.trim() || null,
      });
      reset();
    } catch (err) {
      setErrorMsg(err instanceof ApiError ? err.detail : "Could not record.");
    }
  }

  // Running balance: each payment subtracts from committed in the
  // order they were received. Lets the contractor (and homeowner)
  // see how much was outstanding right after each draw.
  let running = committed;
  const rowsWithRunning = payments.map((p) => {
    running -= p.amount_cents;
    return { p, runningAfter: running };
  });

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Payments
          {payments.length > 0 && (
            <span className="ml-2 normal-case text-graphite-faint">
              ({payments.length})
            </span>
          )}
        </h3>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-2.5 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden /> Record payment
          </button>
        )}
      </div>

      {loading ? (
        <p className="font-mono text-xs text-graphite-soft">Loading…</p>
      ) : payments.length === 0 && !adding ? (
        <p className="rounded border border-rule bg-paper-warm py-4 text-center text-xs text-graphite-soft">
          No payments recorded yet. Add the deposit when received.
        </p>
      ) : (
        <ul className="divide-y divide-rule rounded-lg border border-rule bg-paper-warm">
          {rowsWithRunning.map(({ p, runningAfter }) => (
            <li
              key={p.id}
              className="flex items-start justify-between gap-4 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-xs font-semibold text-graphite-soft">
                    #{p.payment_number}
                  </span>
                  <span className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite">
                    {p.method}
                  </span>
                  <span className="font-mono text-xs text-graphite-soft">
                    {formatTime(p.received_at)}
                  </span>
                </div>
                {p.notes && (
                  <p className="mt-1 text-xs text-graphite-soft">{p.notes}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-mono text-sm font-semibold tabular-nums text-graphite">
                  {formatCents(p.amount_cents)}
                </span>
                <span className="font-mono text-[11px] text-graphite-faint tabular-nums">
                  Balance after: {formatCents(runningAfter)}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete payment #${p.payment_number}?`)) {
                      remove.mutate(p.id);
                    }
                  }}
                  className="text-graphite-soft hover:text-red-pencil"
                  title={`Delete payment #${p.payment_number}`}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="rounded-lg border-2 border-graphite bg-paper p-4 space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="label-mono">Amount ($)</span>
              <input
                type="number"
                step="0.01"
                value={draftAmount}
                onChange={(e) => setDraftAmount(e.target.value)}
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-right font-mono text-sm tabular-nums focus:border-graphite focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="label-mono">Method</span>
              <select
                value={draftMethod}
                onChange={(e) => setDraftMethod(e.target.value)}
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
              >
                <option value="check">Check</option>
                <option value="ach">ACH / e-Check</option>
                <option value="cash">Cash</option>
                <option value="zelle">Zelle</option>
                <option value="venmo">Venmo</option>
                <option value="wire">Wire</option>
                <option value="cc">Credit card</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block">
              <span className="label-mono">Received</span>
              <input
                type="date"
                value={draftDate}
                onChange={(e) => setDraftDate(e.target.value)}
                className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 font-mono text-sm focus:border-graphite focus:outline-none"
              />
            </label>
          </div>
          <label className="block">
            <span className="label-mono">Notes (check #, etc.)</span>
            <input
              type="text"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm focus:border-graphite focus:outline-none"
            />
          </label>
          {errorMsg && (
            <p className="font-mono text-xs text-red-pencil">{errorMsg}</p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={reset}
              className="rounded border border-rule px-3 py-1.5 font-mono text-xs font-semibold text-graphite-soft hover:text-graphite"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={create.isPending}
              className="rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {create.isPending ? "Saving…" : "Record"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
